import React, { PropsWithChildren, createContext, useCallback, useContext, useRef, useState } from "react";

import { Config } from "../environment/config";
import { EnvironmentContext } from "./EnvironmentProvider";
import { HealthMonitorManager } from "@binah/web-sdk/dist/health-monitor-manager";
import { Trans } from "./TranslationProvider";
import { useStateRef } from "../hooks/UseStateRef";
import {
	AlertData,
	DeviceOrientation,
	EnabledVitalSigns,
	FaceSessionOptions,
	HealthMonitorCodes,
	HealthMonitorSession,
	ImageValidity,
	InfoData,
	InfoType,
	OfflineMeasurements,
	SessionState,
	VitalSigns,
	VitalSignsResults
} from "../models/BinahModels";
import { ExecutorUtils } from "@emed-labs/platform-utility-common-utils";

export enum BinahSessionState {
	INACTIVE = "INACTIVE",
	STARTING = "STARTING",
	ACTIVE = "ACTIVE",
	MEASURING = "MEASURING"
}

export interface BinahContext {
	startMeasuring: () => void;
	createFaceSession: (video: React.MutableRefObject<HTMLVideoElement>, cameraId: string) => Promise<void>;
	stopFaceSession: () => void;
	state: BinahSessionState;
	vitalSigns: VitalSigns;
	offlineMeasurements: OfflineMeasurements;
	error: AlertData;
	warning: AlertData;
	info: InfoData;
}

export const BinahContext = createContext<BinahContext>({} as BinahContext);
BinahContext.displayName = "BinahContext";

export const BinahProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
	const EnvCtx = useContext(EnvironmentContext);
	const TransCtx = useContext(Trans.context);

	const [session, setSession, sessionRef] = useStateRef<HealthMonitorSession>();
	const [lastImageValidity, setLastImageValidity, lastImageValidityRef] = useStateRef<ImageValidity>();
	const [state, setState, stateRef] = useStateRef<BinahSessionState>(BinahSessionState.INACTIVE);
	const [enabledVitalSigns, setEnabledVitalSigns] = useState<EnabledVitalSigns>();
	const [offlineMeasurements, setOfflineMeasurements] = useState<OfflineMeasurements>();
	const [vitalSigns, setVitalSigns] = useState<VitalSigns | null>();
	const [isInitialized, setIsInitialized, isInitializedRef] = useStateRef<boolean>(false);

	const [error, setError] = useState<AlertData>({ code: -1 });
	const [warning, setWarning] = useState<AlertData>({ code: -1 });
	const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
	const isDismissing = useRef<boolean>(false);

	const binahWebSDKInstance = useRef<HealthMonitorManager>();

	const setInfoWithDismiss = useCallback(
		(info: InfoData, seconds?: number) => {
			if (!isDismissing.current) {
				setInfo(info);
				if (seconds) {
					isDismissing.current = true;
					setTimeout(() => {
						setInfo({ type: InfoType.NONE });
						isDismissing.current = false;
					}, seconds * 1000);
				}
			}
		},
		[InfoType, setInfo, info, isDismissing, isDismissing.current]
	);

	const updateVitalSigns = useCallback((vitalSigns) => {
		setVitalSigns((prev) => ({
			...prev,
			...vitalSigns
		}));
	}, []);

	const onVitalSign = useCallback((vitalSign: VitalSigns) => {
		if (Object.keys(vitalSign).length === 1 && vitalSign["undefined"]) return;
		updateVitalSigns(vitalSign);
	}, []);

	const onFinalResults = useCallback((vitalSignsResults: VitalSignsResults) => {
		updateVitalSigns({ ...vitalSignsResults.results, isCompleted: true });
		stopFaceSession();
	}, []);

	const onError = (errorData: AlertData) => {
		setError(errorData);
	};

	const onWarning = (warningData: AlertData) => {
		if (warningData.code === HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING) {
			setVitalSigns(null);
		}
		setWarning(warningData);
	};

	const onStateChange = useCallback((state: SessionState) => {
		if (state === SessionState.TERMINATED) {
			setSession(null);
			setState(BinahSessionState.INACTIVE);
		} else if (state === SessionState.MEASURING) {
			setVitalSigns(null);
		}
	}, []);

	const onEnabledVitalSigns = useCallback((vitalSigns: EnabledVitalSigns) => {
		setEnabledVitalSigns(vitalSigns);
	}, []);

	const onOfflineMeasurement = useCallback((offlineMeasurements: OfflineMeasurements) => {
		setOfflineMeasurements(offlineMeasurements);
	}, []);

	const onActivation = useCallback((activationId: string) => {
		// the device has been activated with activationId
	}, []);

	const onImageData = useCallback((imageValidity: ImageValidity) => {
		if (imageValidity === lastImageValidityRef.current) return;
		let message: string;
		if (imageValidity != ImageValidity.VALID) {
			switch (imageValidity) {
				case ImageValidity.INVALID_DEVICE_ORIENTATION:
					message = TransCtx.lookup("widgets.binah.unsupported-orientation");
					break;
				case ImageValidity.TILTED_HEAD:
					message = TransCtx.lookup("widgets.binah.head-tilted");
					break;
				case ImageValidity.FACE_TOO_FAR: // Placeholder, currently not supported
					message = TransCtx.lookup("widgets.binah.too-far");
					break;
				case ImageValidity.UNEVEN_LIGHT:
					message = TransCtx.lookup("widgets.binah.uneven-lighting");
					break;
				case ImageValidity.INVALID_ROI:
				default:
					message = TransCtx.lookup("widgets.binah.face-not-detected");
			}
			setInfo({
				type: InfoType.INSTRUCTION,
				message: message
			});
		} else {
			setInfoWithDismiss({ type: InfoType.NONE });
		}
		setLastImageValidity(imageValidity);
	}, []);

	const initialize = async (): Promise<void> => {
		if (isInitializedRef.current && binahWebSDKInstance.current) {
			return;
		}
		try {
			await import("@binah/web-sdk").then(async (monitor) => {
				binahWebSDKInstance.current = monitor.default;
				await monitor.default.initialize({
					licenseKey: EnvCtx.get(Config.BINAH_LICENSE_KEY),
					licenseInfo: {
						onEnabledVitalSigns,
						onOfflineMeasurement,
						onActivation
					}
				});
				setError({ code: -1 });
				setIsInitialized(true);
			});
		} catch (e) {
			setError({ code: e.errorCode });
		}
	};

	const stopFaceSession = async (): Promise<void> => {
		if (sessionRef.current) {
			sessionRef.current.terminate();
			// because the session is terminated asynchronously, we need to wait for it to be terminated
			// there is an event listener available however ensuring asynchronous wait in functional components is difficult
			// this is a quick solution for the problem; a more elegant solution is needed
			for (;;) {
				if (sessionRef.current?.getState() === SessionState.TERMINATED) {
					break;
				}
				await ExecutorUtils.schedule(() => {}, 50).promise();
			}
		}
		setSession(null);
		setState(BinahSessionState.INACTIVE);
	};

	const createFaceSession = async (video: React.MutableRefObject<HTMLVideoElement>, cameraId: string) => {
		try {
			await initialize();

			if (!video.current || !cameraId) {
				return;
			}

			if (sessionRef.current) {
				sessionRef.current.terminate();
			}

			setState(BinahSessionState.STARTING);

			const options: FaceSessionOptions = {
				input: video.current as HTMLVideoElement,
				cameraDeviceId: cameraId,
				processingTime: 120,
				onVitalSign,
				onFinalResults,
				onError,
				onWarning,
				onStateChange,
				orientation: DeviceOrientation.PORTRAIT,
				onImageData
				/*******************************************************************************
				 * For accurate vital signs calculation the user's parameters should be provided.
				 * The following is an example of how the parameters are passed to the SDK:
				 *
				 * subjectDemographic: {age: 35, weight: 75, gender: Gender.MALE}
				 *
				 * When measuring a new user then a new session must be created using the
				 * new user's parameters
				 * The parameters are used locally on the device only for the vital sign
				 * calculation, and deleted when the session is terminated.
				 *******************************************************************************/
			};

			const faceSession = await binahWebSDKInstance.current.createFaceSession(options);
			setSession(faceSession);
			setError({ code: -1 });
			setState(BinahSessionState.ACTIVE);
		} catch (e) {
			setError({ code: e.errorCode });
			setState(BinahSessionState.INACTIVE);
		}
	};

	const startMeasuring = async () => {
		if (sessionRef.current?.getState() === SessionState.ACTIVE) {
			sessionRef.current.start();
			// because the session is started asynchronously, we need to wait for it to be terminated
			// there is an event listener available however ensuring asynchronous wait in functional components is difficult
			// this is a quick solution for the problem; a more elegant solution is needed
			for (;;) {
				if (sessionRef.current.getState() === SessionState.MEASURING) {
					break;
				}
				await ExecutorUtils.schedule(() => {}, 50).promise();
			}
			setError({ code: -1 });
			setState(BinahSessionState.MEASURING);
		}
	};

	const value: BinahContext = {
		startMeasuring,
		createFaceSession,
		stopFaceSession,
		state,
		vitalSigns,
		offlineMeasurements,
		error,
		warning,
		info
	};

	return <BinahContext.Provider value={value}>{props.children}</BinahContext.Provider>;
};
